import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { getAuthHeaders } from '../utils/auth';
import { FOUNDER_EMAILS } from '../config';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Vibrant colors from RetroWordmark
const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

const TokenPackOption = ({ name, price, tokens, pricePerToken, savings, onPurchase, isProcessing, backgroundImage, isAuthenticated, colorIndex }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Random rotations that change on hover
  const [randomRotation] = useState(() => Math.random() * 4 - 2);
  const [bgRandomRotation] = useState(() => Math.random() * 6 - 3);
  const [hoverRotation, setHoverRotation] = useState(0);
  const [bgHoverRotation, setBgHoverRotation] = useState(0);

  // Generate new random rotations on hover
  useEffect(() => {
    if (isHovered) {
      setHoverRotation(Math.random() * 8 - 4); // -4 to +4 degrees
      setBgHoverRotation(Math.random() * 12 - 6); // -6 to +6 degrees
    }
  }, [isHovered]);

  return (
    <motion.div
      className="relative group h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background card */}
      <motion.div
        className="absolute inset-0 rounded-lg shadow-lg"
        style={{
          background: colors[colorIndex],
          transformOrigin: 'center center',
          zIndex: 1
        }}
        initial={{ 
          rotate: bgRandomRotation,
          scale: 1.05,
          opacity: 0.8
        }}
        animate={{ 
          rotate: isHovered ? bgHoverRotation : bgRandomRotation,
          scale: isHovered ? 1.08 : 1.05,
          opacity: isHovered ? 0.9 : 0.8
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      />

      {/* Main card */}
      <motion.div
        className="relative rounded-lg overflow-hidden group h-full"
        style={{
          zIndex: 2
        }}
        animate={{
          rotate: isHovered ? hoverRotation : randomRotation,
          scale: isHovered ? 1.02 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          {backgroundImage ? (
            <img 
              src={backgroundImage} 
              alt="" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-black/5" />
          )}
          {/* Subtle dark overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to top, 
                rgba(0, 0, 0, 0.75), 
                rgba(0, 0, 0, 0.5)
              )`
            }}
          />
        </div>

        {/* Content */}
        <div className="relative p-4 backdrop-blur-sm h-full flex flex-col">
          <div className="flex-grow">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-xl font-['VT323'] text-white">{name}</h3>
              <div className="text-2xl font-['VT323'] text-white">${price}</div>
            </div>

            <div className="flex flex-col gap-1 text-white/90 mb-4">
              <div className="font-['VT323'] text-sm">{pricePerToken}¢ per token</div>
              {savings && (
                <div className="font-['VT323'] text-sm text-[#00FF9F] font-bold">{savings}% savings</div>
              )}
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => isAuthenticated ? onPurchase() : toast.error('Please log in to purchase tokens')}
            disabled={isProcessing || !isAuthenticated}
            className={`w-full py-2 px-4 rounded-lg font-['VT323'] text-lg transition-colors
              ${!isAuthenticated
                ? 'bg-white/20 text-white/60 cursor-not-allowed'
                : isProcessing
                  ? 'bg-white/20 text-white cursor-not-allowed'
                  : 'bg-white hover:bg-opacity-90 text-black'
              }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/20 border-t-white/40 rounded-full animate-spin" />
              </div>
            ) : !isAuthenticated ? (
              'Login to Purchase'
            ) : (
              'Buy Now'
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const TokenPacks = () => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPack, setProcessingPack] = useState(null);
  const [founderImages, setFounderImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  // Shuffle colors on mount
  const [shuffledColors] = useState(() => {
    return [...colors].sort(() => Math.random() - 0.5);
  });

  const tokenPacks = [
    {
      name: '100 Tokens',
      price: 12,
      tokens: 100,
      pricePerToken: 12,
      savings: null
    },
    {
      name: '500 Tokens',
      price: 49,
      tokens: 500,
      pricePerToken: 9.8,
      savings: 18
    },
    {
      name: '1000 Tokens',
      price: 89,
      tokens: 1000,
      pricePerToken: 8.9,
      savings: 26
    }
  ];

  useEffect(() => {
    const fetchFounderImages = async () => {
      try {
        setIsLoadingImages(true);
        
        // Try each founder in sequence until we get images
        for (const founder of FOUNDER_EMAILS) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/favorites/public/founder?founder=${encodeURIComponent(founder)}`);
            
            if (!response.ok) {
              console.warn(`Failed to fetch images for founder ${founder}`);
              continue;
            }
            
            const data = await response.json();
            if (data && data.length > 0) {
              // Shuffle the images and take a few for backgrounds
              const shuffled = [...data].sort(() => Math.random() - 0.5);
              setFounderImages(shuffled.slice(0, 3).map(img => ({
                ...img,
                url: img.thumbnailUrl || img.s3Url || img.url
              }))); // Get 3 images for pack backgrounds
              break; // Exit the loop once we have images
            }
          } catch (error) {
            console.warn(`Error fetching images for founder ${founder}:`, error);
          }
        }
      } catch (error) {
        console.error('Error fetching founder images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    fetchFounderImages();
  }, []);

  const handlePurchase = async (packName, price, tokens) => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase tokens');
      loginWithRedirect();
      return;
    }

    if (isProcessing) return;
    setIsProcessing(true);
    setProcessingPack(packName);

    try {
      const headers = await getAuthHeaders(getAccessTokenSilently);
      if (!headers) {
        throw new Error('Failed to get authentication headers');
      }

      const response = await fetch(`${API_URL}/api/tokens/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ packName, price, tokens })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      if (error.message === 'Failed to get authentication headers') {
        toast.error('Please log in again to purchase tokens');
        loginWithRedirect();
      } else {
        toast.error('Failed to initiate purchase. Please try again.');
      }
    } finally {
      setIsProcessing(false);
      setProcessingPack(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Title */}
      <div className="text-center">
        <h2 className="text-2xl font-['VT323'] text-black/80 mb-2">need a topup?</h2>
        <p className="text-sm text-black/60 font-['VT323']">Add more tokens to your existing monthly allowance</p>
      </div>

      {/* Token Packs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tokenPacks.map(({ name, price, tokens, pricePerToken, savings }, index) => (
          <TokenPackOption
            key={name}
            name={name}
            price={price}
            tokens={tokens}
            pricePerToken={pricePerToken}
            savings={savings}
            backgroundImage={founderImages[index]?.url}
            onPurchase={() => handlePurchase(name, price, tokens)}
            isProcessing={processingPack === name}
            isAuthenticated={isAuthenticated}
            colorIndex={index % colors.length}
          />
        ))}
      </div>
    </div>
  );
};

export default TokenPacks; 